import {
  deepCopyDate,
  getFormattedDuration,
  getISODateString,
  getIstTimeFromTimestamp,
  getTimestampFromDatetime,
} from "@/methods/utils";
import { ServerClock, ServerClockStore } from "@/types/global";
import { differenceInSeconds } from "date-fns";
import { defineStore } from "pinia";
import { useApiServicesStore } from "@/stores/core/apiServicesStore";
import { v4 as uuidv4 } from "uuid";

function getInitialServerClockStore(): ServerClockStore {
  const initialServerClockStore: ServerClockStore = {
    LastServerClock: null,
    ServerClockWorkerUUID: "",
    ServerClockWorker: null,
    _ServerTimestamp: 0,
  };
  return JSON.parse(JSON.stringify(initialServerClockStore));
}

export const useServerClockStore = defineStore("serverClockStore", {
  state: (): ServerClockStore => getInitialServerClockStore(),
  getters: {
    CurrentWorkerKey(): string {
      return `SWC_${this.ServerClockWorkerUUID}`;
    },
    UpdatedServerClock(): Date {
      return this.LastServerClock
        ? getIstTimeFromTimestamp(this.LastServerClock.Timestamp)
        : new Date(0);
    },
    UpdatedServerDate(): Date {
      const serverDate = deepCopyDate(this.UpdatedServerClock);
      serverDate.setHours(0, 0, 0, 0);
      return serverDate;
    },
    UpdatedServerDatetimeString(): string | null {
      const isoString = getISODateString(this.UpdatedServerClock);
      return isoString ? isoString : null;
    },
  },
  actions: {
    async pingServerAndInitializeClock(): Promise<number> {
      this.resetServerClock();
      const apiServicesStore = useApiServicesStore();
      const { unverifiedUserApi } = apiServicesStore;
      const response = await unverifiedUserApi.getServerClock();
      if (response.isSuccess) {
        this.LastServerClock = response.data as ServerClock;
        this._ServerTimestamp = this.LastServerClock.Timestamp;
        this.ServerClockWorkerUUID = uuidv4().replace(/-/g, "").slice(0, 8);
        this.ServerClockWorker = new Worker("/serverClockWorker.js");
        (window as any)[this.CurrentWorkerKey] = this.ServerClockWorker;
        this.ServerClockWorker.addEventListener("message", (e) => {
          const { type, timestamp, workerKey } = e.data;
          if (workerKey !== this.CurrentWorkerKey) {
            console.debug(
              `Stopping old worker:: ${workerKey} vs Current: ${this.CurrentWorkerKey}`,
            );
            this.stopWorkerByKey(workerKey);
            return;
          }
          if (type === "CLOCK_UPDATE") {
            this._ServerTimestamp = timestamp;
          }
        });
        this.ServerClockWorker.postMessage({
          type: "START_CLOCK",
          data: {
            workerKey: this.CurrentWorkerKey,
            initialServerTimestamp: this._ServerTimestamp,
          },
        });
      }
      return response.statusCode;
    },
    resetServerClock() {
      this.stopWorkerByKey(this.CurrentWorkerKey);
      if (this.ServerClockWorker) {
        this.stopServerClockWorker(this.ServerClockWorker);
      }
      this.$reset();
    },
    stopWorkerByKey(workerKey: string) {
      const worker = (window as any)[workerKey];
      if (worker) {
        this.stopServerClockWorker(worker);
      }
    },
    stopServerClockWorker(worker: Worker) {
      try {
        worker.postMessage({ type: "STOP_CLOCK" });
        worker.terminate();
        delete (window as any)[this.CurrentWorkerKey];
      } catch (err) {
        console.warn("Failed to stop server clock worker:", err);
      }
    },
    async getServerTimestamp(): Promise<number> {
      await this._waitForServerSync();
      return this._ServerTimestamp;
    },
    getTimeRemaining(endDatetime: string, maxDurationMinutes: number) {
      if (this._ServerTimestamp === null) {
        return null;
      }
      const endTimestamp = getTimestampFromDatetime(endDatetime);
      const timeDifference = differenceInSeconds(endTimestamp, this._ServerTimestamp);
      const timeRemaining = Math.min(maxDurationMinutes * 60, timeDifference);
      return Math.max(0, timeRemaining);
    },
    getTimeElapsed(startTimestamp: number) {
      if (this._ServerTimestamp === null) {
        return null;
      }
      const timeDifference = differenceInSeconds(this._ServerTimestamp, startTimestamp);
      return Math.max(0, timeDifference);
    },
    _waitForServerSync(): Promise<void> {
      if (this._ServerTimestamp !== null && this._ServerTimestamp > 0) {
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        this.pingServerAndInitializeClock();
        const unsubscribe = this.$subscribe(() => {
          if (this._ServerTimestamp !== null && this._ServerTimestamp > 0) {
            unsubscribe();
            resolve();
          }
        });
      });
    },
    getTimerDisplay(timeValue: number | null): string | null {
      return timeValue === null ? null : getFormattedDuration(timeValue);
    },
  },
});
